import { FC } from "react"
import { Card, CardContent, CardHeader } from "./ui/card"
import { Badge } from "./ui/badge"
import { Button } from "./ui/button"
import { AlertTriangle, Settings, Droplets } from "lucide-react"
import { useNavigate, useParams } from "react-router-dom"
import { TableDateDisplay } from "./ui/date-display"
import { formatDeviceType } from "@/lib/deviceType"

interface DeviceCardProps {
  device: {
    id: string
    name: string | null
    serialNumber: string
    type: string | null
    status: string
    location: string | null
    lastReading?: string
    lastUpdated: string | null
    batteryLevel: number
    signalStrength: number
    leakAlarms: number
  }
}

const getDeviceTypeIcon = (type: string | null) => {
  return type === "smart_meter" ? Settings : Droplets
}

const getSignalStrengthInfo = (rssi: number) => {
  if (rssi > -65) {
    return { level: 5, description: "5/5 (Very Good)" }
  } else if (rssi >= -75) {
    return { level: 4, description: "4/5 (Good)" }
  } else if (rssi >= -85) {
    return { level: 3, description: "3/5 (Average)" }
  } else if (rssi >= -95) {
    return { level: 2, description: "2/5 (Weak)" }
  } else {
    return { level: 1, description: "1/5 (Very Weak)" }
  }
}

const DeviceCard: FC<DeviceCardProps> = ({ device }) => {
  const navigate = useNavigate()
  const { councilId } = useParams()
  const DeviceIcon = getDeviceTypeIcon(device.type)
  const hasAlarms = device.leakAlarms > 0
  const signalInfo = getSignalStrengthInfo(device.signalStrength)

  return (
    <Card
      key={device.id}
      className={`relative ${hasAlarms ? "border-red-200" : "border-blue-200"}`}
    >
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2">
            <div
              className={`p-1 rounded ${hasAlarms ? "bg-red-100 text-red-600" : device.type === "smart_meter" ? "bg-blue-100 text-blue-600" : "bg-green-100 text-green-600"}`}
            >
              <DeviceIcon className="size-4" />
            </div>
            <Badge
              variant={device.type === "smart_meter" ? "default" : "secondary"}
              className="text-xs"
            >
              {formatDeviceType(device.type)}
            </Badge>
          </div>
          {hasAlarms && <AlertTriangle className="size-4 text-red-500" />}
        </div>

        <div>
          <h3 className="font-semibold text-sm leading-tight">{device.name || "Unnamed Device"}</h3>
          <p className="text-xs text-muted-foreground">{device.serialNumber}</p>
        </div>
      </CardHeader>

      <CardContent className="pt-0">
        <div className="space-y-2 text-xs">
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Location:</span>
            <span>{device.location || "Unknown"}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Last Updated:</span>
            <span className="font-medium">
              <TableDateDisplay date={device.lastUpdated} />
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Battery:</span>
            <span className={device.batteryLevel < 20 ? "text-red-500" : "text-green-500"}>
              {device.batteryLevel}%
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Signal:</span>
            <span>{signalInfo.description}</span>
          </div>
          {hasAlarms && (
            <div className="flex items-center justify-between text-red-600">
              <span>Alarms:</span>
              <span className="font-medium">{device.leakAlarms}</span>
            </div>
          )}
        </div>

        <div className="mt-4 pt-3 border-t">
          <Button
            size="sm"
            variant="outline"
            className="w-full hover-scale"
            onClick={() => navigate(`/council/${councilId}/devices/${device.id}`)}
          >
            View Details
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

export default DeviceCard
